import { Sparkles } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import { AdditiveBlending, Color, Group, Mesh, MeshBasicMaterial, Vector3 } from 'three';
import { LightningStrike, type RayParameters } from 'three-stdlib';

/** Fallback offset (only used when bone-attachment fails). */
const HAND_LOCAL: [number, number, number] = [0.25, 1.05, 0.10];

const BOLT_COLOR = '#ffd84a';
const BOLT_LENGTH = 0.95;            // length along bone +Y after rotation
const STRIKE_COUNT = 3;              // number of overlapping eternal bolts
const SPREAD = 0.18;                 // ±18cm dest jitter so the strikes diverge

/**
 * Lightning weapon hand-orb — three eternal LightningStrike bolts crackling
 * outward from the palm, plus tip sparkles + base halo + point-light glow.
 *
 * Replaces the previous pre-baked TubeGeometry variant-swap (which felt
 * static because the same 6 frames cycled). LightningStrike's procedural
 * jagged path with built-in branching gives proper electric arcs.
 *
 * Pattern same as HeartSparkBurst.tsx: imperative pool of Mesh<LightningStrike>
 * added in useEffect, animated each frame via strike.update(t).
 */

type LightningStrikeWithParams = LightningStrike & { rayParameters: RayParameters };

interface BoltSlot {
  strike: LightningStrikeWithParams;
  mesh: Mesh;
  endX: number;
  endZ: number;
}

interface Props {
  /** True when portaled into a hand bone — applies a local rotation so the
   *  bolt extends along the character's facing direction. */
  boneAttached?: boolean;
}

export function LightningHandOrb({ boneAttached = false }: Props) {
  const outerGroupRef = useRef<Group>(null);
  const boltGroupRef = useRef<Group>(null);
  const slotsRef = useRef<BoltSlot[]>([]);
  const clock = useThree((s) => s.clock);
  const elapsed = useRef(0);

  useEffect(() => {
    const boltGroup = boltGroupRef.current;
    if (!boltGroup) return;

    const slots: BoltSlot[] = [];
    for (let i = 0; i < STRIKE_COUNT; i++) {
      // Each strike points along bone +Y with a small XZ offset so the three
      // bolts visibly diverge at the tip instead of overlapping perfectly.
      const angle = (i / STRIKE_COUNT) * Math.PI * 2;
      const endX = Math.cos(angle) * SPREAD;
      const endZ = Math.sin(angle) * SPREAD;
      const params: RayParameters = {
        sourceOffset: new Vector3(0, 0, 0),
        destOffset: new Vector3(endX, BOLT_LENGTH, endZ),
        radius0: 0.012,
        radius1: 0.004,
        minRadius: 0.001,
        maxIterations: 5,
        isEternal: true,                 // continuously animates
        timeScale: 0.9,
        propagationTimeFactor: 0.05,
        vanishingTimeFactor: 0.95,
        subrayPeriod: 0.4 + i * 0.12,   // de-sync the three bolts
        subrayDutyCycle: 0.5,
        maxSubrayRecursion: 2,
        ramification: 5,
        recursionProbability: 0.55,
        roughness: 0.85,
        straightness: 0.55,
      };
      const strike = new LightningStrike(params) as LightningStrikeWithParams;
      const mesh = new Mesh(
        strike,
        new MeshBasicMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: 1,
          blending: AdditiveBlending,
          depthWrite: false,
          toneMapped: false,
        }),
      );
      mesh.frustumCulled = false;
      boltGroup.add(mesh);
      slots.push({ strike, mesh, endX, endZ });
    }
    slotsRef.current = slots;

    return () => {
      for (const s of slots) {
        boltGroup.remove(s.mesh);
        (s.mesh.material as MeshBasicMaterial).dispose();
        s.strike.dispose();
      }
      slotsRef.current = [];
    };
  }, []);

  useFrame((_, dt) => {
    elapsed.current += dt;
    const t = clock.getElapsedTime();

    // Drive each LightningStrike — update() recomputes the jagged path
    for (const slot of slotsRef.current) {
      slot.strike.update(t);
    }

    // Subtle whole-bolt-pulse so the eye reads it as alive
    if (boltGroupRef.current) {
      const pulse = 1 + 0.05 * Math.sin(elapsed.current * 14);
      boltGroupRef.current.scale.setScalar(pulse);
      boltGroupRef.current.rotation.y = Math.sin(elapsed.current * 6) * 0.04;
    }
  });

  // Bone-attached: rotate to align bone +Y with character forward.
  // Same Euler used by legendary KayKit weapons in items.ts.
  const containerProps = boneAttached
    ? { position: [0, 0, 0] as [number, number, number], rotation: [0, 0.96, -Math.PI / 2] as [number, number, number] }
    : { position: HAND_LOCAL };

  return (
    <group ref={outerGroupRef} {...containerProps}>
      <group ref={boltGroupRef} />
      {/* Tip sparkles + base palm-halo + ground light kept from previous design
          — these read as the "ambient discharge" around the bolt. */}
      <Sparkles
        position={[0, BOLT_LENGTH, 0]}
        count={14}
        size={3}
        scale={[0.35, 0.35, 0.35]}
        speed={1.6}
        color={new Color(BOLT_COLOR)}
        opacity={0.9}
      />
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, 0.02, 0]}>
        <circleGeometry args={[0.15, 28]} />
        <meshBasicMaterial
          color={BOLT_COLOR}
          transparent
          opacity={0.55}
          blending={AdditiveBlending}
          depthWrite={false}
          toneMapped={false}
        />
      </mesh>
      <pointLight color={BOLT_COLOR} intensity={2.2} distance={3.0} />
    </group>
  );
}
